// Ligação entre login de professor (club_staff) e cadastro de professor
// (club_teachers). Ver lib/vinculo-professor.ts para o porquê.
import { useQuery } from '@tanstack/react-query';
import { useDomainMutation } from '@pps/mutations';
import { supabase } from '@/integrations/supabase/client';
import { useScopedClubIds } from '@/hooks/useScopedClubIds';
import { TEACHERS_KEY } from '@/hooks/useTeachers';
import i18n from '@/i18n';
import { chaveDoMotivo, motivoDoVinculo } from '@/lib/vinculo-professor';

export interface LoginDeProfessorSemCadastro {
  club_id: string;
  user_id: string;
  full_name: string | null;
  email: string | null;
}

export const SEM_CADASTRO_KEY = ['teacher-logins-without-record'] as const;
const CLUB_STAFF_KEY = ['club-staff'] as const;

/**
 * Logins de professor ativos sem cadastro ligado nos clubes do escopo. Gestor
 * (staff.manage/teachers.manage) recebe todos; o professor, só a própria linha
 * — é assim que o aviso dele aparece.
 */
export function useTeacherLoginsWithoutRecord(opts: { enabled?: boolean } = {}) {
  const { scopedClubIds } = useScopedClubIds();
  return useQuery<LoginDeProfessorSemCadastro[]>({
    queryKey: [...SEM_CADASTRO_KEY, scopedClubIds],
    enabled: (opts.enabled ?? true) && scopedClubIds.length > 0,
    staleTime: 60_000,
    queryFn: async () => {
      const { data, error } = await supabase.rpc('teacher_logins_without_record', {
        p_club_ids: scopedClubIds,
      });
      if (error) throw error;
      return (data ?? []) as LoginDeProfessorSemCadastro[];
    },
  });
}

export interface LinkTeacherLoginInput {
  clubId: string;
  userId: string;
  teacherId: string | null;
  teacherName: string | null;
}

/** Erro da ligação já traduzido (motivo conhecido) ou o original. */
export function erroDaLigacao(error: unknown): Error {
  const texto = error && typeof error === 'object' && 'message' in error
    ? String((error as { message?: unknown }).message ?? '')
    : '';
  const motivo = motivoDoVinculo(texto);
  if (motivo) return new Error(i18n.t(chaveDoMotivo(motivo)));
  return error instanceof Error ? error : new Error(texto || i18n.t('common.error'));
}

/**
 * @param opts.silencioso sem toast (nem de sucesso nem de erro): para quem
 *   chama dentro de um fluxo que já avisa — o diálogo de Equipe mostra o erro
 *   no próprio catch, e dois toasts do mesmo erro confundem.
 */
export function useLinkTeacherLogin(opts: { silencioso?: boolean } = {}) {
  return useDomainMutation<string, Error, LinkTeacherLoginInput>({
    mutationFn: async ({ clubId, userId, teacherId, teacherName }) => {
      const { data, error } = await supabase.rpc('link_teacher_login', {
        p_club_id: clubId,
        p_user_id: userId,
        p_teacher_id: teacherId,
        p_name: teacherName,
      });
      if (error) throw erroDaLigacao(error);
      if (!data) throw new Error(i18n.t('common.error'));
      return data;
    },
    invalidates: [TEACHERS_KEY, CLUB_STAFF_KEY, SEM_CADASTRO_KEY],
    toast: opts.silencioso ? false : { success: i18n.t('teachers.link.linkedSuccess'), error: i18n.t('common.error') },
    tags: { feature: 'crm', action: 'link_teacher_login' },
  });
}
